import React, {useEffect, useRef, useState} from "react";
import {useApolloClient} from "@apollo/client";
import {
  Button,
  Callout,
  Checkbox,
  Collapse,
  DialogBody,
  DialogFooter,
  Intent,
  Menu,
  MenuItem,
  Popover,
  ProgressBar,
  Spinner,
  Tooltip
} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import log from "loglevel";
import {COLORS, Constants} from "../../../constants/constants";
import {CancelButton, StyledButton} from "../../common/StyledButtons";
import {StyledDialog} from "../styles/styleds";
import {findCustomerCodes, getCsvImportKnownValues} from "./CsvImport.api";
import {
  CSV_IMPORT_COLUMNS,
  CSV_IMPORT_EXAMPLE,
  CSV_IMPORT_EXAMPLE_TXT,
  CSV_IMPORT_REQUIRED_LABELS,
  createCsvImportOrders,
  CsvImportCreateError,
  isAbortError,
  parseCsvImportFile
} from "./CsvImport.service";
import CsvImportJobsTable from "./CsvImportJobsTable";
import CsvImportStopsTable from "./CsvImportStopsTable";
import CsvImportTimeline from "./CsvImportTimeline";
import {
  CsvImportCreatedOrder,
  CsvImportCreatePhase,
  CsvImportFailedOrder,
  CsvImportKnownValues,
  CsvImportProgress,
  CsvImportResult
} from "./types";

type CsvImportDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  // Lets the caller show progress, then a done or failed status, next to the Upload button while the dialog is closed
  onProgressChange?: (progress: CsvImportProgress) => void;
  // Stops already selected for planning, to check the planning stop limit before adding the created jobs
  selectedStopCount: number;
  // Adds the created jobs to the planning selection
  onSelectForPlanning: (orders: CsvImportCreatedOrder[]) => void;
};

type Stage = "select" | "preview" | "create";
type CreateStatus = CsvImportCreatePhase | "done" | "failed";

const STAGE_INDEX: Record<Stage, number> = {select: 0, preview: 1, create: 2};
const HINT_ID = "csv-import-hint";
// Progress bar while orders are being created: starts here, full once every order exists
const CREATE_PROGRESS_MIN = 0.03;
const EXAMPLE_FILE_NAME = "itinerary-planning-import-example";
const OPTIONAL_LABELS = CSV_IMPORT_COLUMNS.filter((column) => !column.required).map((column) => column.label);

// What the status next to the Upload button shows for each creation status
const toProgressState = (status?: CreateStatus): CsvImportProgress["state"] => {
  if (status === "creating" || status === "syncing") return "creating";
  if (status === "done" || status === "failed") return status;
  return "idle";
};

const plural = (count: number, word: string) => `${count.toLocaleString("en-US")} ${word}${count === 1 ? "" : "s"}`;

const CsvImportDialog = ({
  isOpen,
  onClose,
  onProgressChange,
  selectedStopCount,
  onSelectForPlanning
}: CsvImportDialogProps) => {
  const [stage, setStage] = useState<Stage>("select");
  const [fileName, setFileName] = useState<string>();
  const [isParsing, setIsParsing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isExampleOpen, setIsExampleOpen] = useState(false);
  const [result, setResult] = useState<CsvImportResult>();
  const [createStatus, setCreateStatus] = useState<CreateStatus>();
  const [createCounts, setCreateCounts] = useState({created: 0, total: 0, failed: 0});
  const [showFailedOrders, setShowFailedOrders] = useState(false);
  const [createdOrders, setCreatedOrders] = useState<CsvImportCreatedOrder[]>([]);
  const [failReason, setFailReason] = useState<CsvImportCreateError["reason"]>();
  // Which orders CMS rejected and why, and the import-level error when the import failed as a whole
  const [failedOrders, setFailedOrders] = useState<CsvImportFailedOrder[]>([]);
  const [failDetail, setFailDetail] = useState<string>();
  const apolloClient = useApolloClient();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [showConfirmError, setShowConfirmError] = useState(false);
  const confirmInputRef = useRef<HTMLInputElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  // Bumped on every pick and reset, so a parse that finishes late can't overwrite newer state
  const parseIdRef = useRef(0);
  // Stops polling the creation status when the dialog unmounts (e.g. leaving Itinerary Planning)
  const pollAbortRef = useRef<AbortController>();
  useEffect(() => () => pollAbortRef.current?.abort(), []);

  // The tenant's services and vehicle types, loaded once when Itinerary Planning opens (the dialog is mounted
  // with it); the file's Service and Vehicle are checked against them. A failed load is retried by the next pick
  const knownValuesRef = useRef<Promise<CsvImportKnownValues>>();
  useEffect(() => {
    const loading = getCsvImportKnownValues(apolloClient);
    knownValuesRef.current = loading;
    loading.catch((error) => {
      log.warn("Failed to load the services and vehicle types", error);
      if (knownValuesRef.current === loading) knownValuesRef.current = undefined;
    });
  }, [apolloClient]);

  const isCreating = createStatus === "creating" || createStatus === "syncing";
  const progressState = toProgressState(createStatus);
  useEffect(() => {
    onProgressChange?.({state: progressState, created: createCounts.created, total: createCounts.total});
  }, [progressState, createCounts, onProgressChange]);

  // A step change unmounts the button that had focus; move focus to the new step so keyboard users keep their place
  const isFirstStageRef = useRef(true);
  useEffect(() => {
    if (isFirstStageRef.current) {
      isFirstStageRef.current = false;
      return;
    }
    timelineRef.current?.querySelectorAll<HTMLElement>("li")[STAGE_INDEX[stage]]?.focus();
  }, [stage]);

  const reset = () => {
    parseIdRef.current += 1;
    setStage("select");
    setFileName(undefined);
    setIsParsing(false);
    setIsExampleOpen(false);
    setResult(undefined);
    setCreateStatus(undefined);
    setCreateCounts({created: 0, total: 0, failed: 0});
    setShowFailedOrders(false);
    setCreatedOrders([]);
    setFailReason(undefined);
    setFailedOrders([]);
    setFailDetail(undefined);
    setIsConfirmed(false);
    setShowConfirmError(false);
  };

  // Closing while orders are being created only hides the dialog (creation can't be cancelled) and
  // reopening continues on step 3; any other close starts the next import fresh
  const handleClose = () => {
    if (!isCreating) reset();
    onClose();
  };

  const handleSelectForPlanning = () => {
    onSelectForPlanning(createdOrders);
    reset();
    onClose();
  };

  const handleFile = async (file: File) => {
    const parseId = ++parseIdRef.current;
    setFileName(file.name);
    setResult(undefined);
    setIsParsing(true);
    let parsed: CsvImportResult;
    let known: CsvImportKnownValues | undefined;
    let issue = "The services and vehicle types could not be loaded. Choose the file again";
    try {
      if (!knownValuesRef.current) knownValuesRef.current = getCsvImportKnownValues(apolloClient);
      known = await knownValuesRef.current;
      issue = "The file could not be read";
      // Customers are looked up by the file's own codes, then checked with the other columns
      parsed = await parseCsvImportFile(file, known, (codes) => {
        issue = "The customer codes could not be checked. Choose the file again";
        return findCustomerCodes(apolloClient, codes);
      });
    } catch (error) {
      log.error("Failed to read CSV import file", error);
      if (!known) knownValuesRef.current = undefined;
      parsed = {orders: [], issues: [issue], ignoredColumns: [], stopCount: 0};
    }
    if (parseId !== parseIdRef.current) return;
    setIsParsing(false);
    setResult(parsed);
    // Invalid files stay on the first step with their issues
    if (parsed.issues.length === 0 && parsed.orders.length > 0) setStage("preview");
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    // Reset so picking the same file again after editing it still fires onChange
    event.currentTarget.value = "";
    if (file) void handleFile(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const handleDownloadExample = (extension: "csv" | "txt") => {
    const [content, type] =
      extension === "csv" ? [CSV_IMPORT_EXAMPLE, "text/csv"] : [CSV_IMPORT_EXAMPLE_TXT, "text/plain"];
    const url = URL.createObjectURL(new Blob([content], {type}));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${EXAMPLE_FILE_NAME}.${extension}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Create stays clickable so the confirmation can't be missed: an unticked box is flagged and focused
  const handleCreateClick = () => {
    if (!isConfirmed) {
      setShowConfirmError(true);
      confirmInputRef.current?.focus();
      return;
    }
    void handleConfirm();
  };

  const handleConfirm = async () => {
    if (!result) return;
    setStage("create");
    setCreateStatus("creating");
    setCreateCounts({created: 0, total: result.orders.length, failed: 0});
    pollAbortRef.current = new AbortController();
    try {
      const created = await createCsvImportOrders(
        apolloClient,
        result.orders,
        ({phase, created: createdCount, total, failed}) => {
          setCreateStatus(phase);
          setCreateCounts({created: createdCount, total, failed: failed ?? 0});
        },
        pollAbortRef.current.signal
      );
      setCreatedOrders(created);
      setCreateStatus("done");
    } catch (error) {
      // Unmounted: nothing left to update
      if (isAbortError(error)) return;
      log.error("Failed to create orders from CSV import", error);
      if (error instanceof CsvImportCreateError) {
        setFailReason(error.reason);
        setCreateCounts({created: error.created, total: error.total, failed: error.failedOrders.length});
        setFailedOrders(error.failedOrders);
        // Orders that were created and are ready for planning can still be selected
        setCreatedOrders(error.createdOrders);
        setFailDetail(error.detail);
      }
      setCreateStatus("failed");
    }
  };

  const orderCount = result?.orders.length ?? 0;
  // Never an empty bar: the submit and the first order take a while, so start part-way; full once every
  // order exists (the ready check then runs under the "waiting" title)
  const progressValue =
    createStatus === "syncing"
      ? 1
      : CREATE_PROGRESS_MIN +
        (createCounts.total ? createCounts.created / createCounts.total : 0) * (1 - CREATE_PROGRESS_MIN);
  const maxStops = Constants.OPTIMIZATION.MAX_STOPS;
  const createdStopCount = createdOrders.reduce((total, order) => total + order.stops.length, 0);
  const isOverStopLimit = selectedStopCount + createdStopCount > maxStops;
  const stopLimitMessage =
    `Itinerary Planning allows up to ${maxStops.toLocaleString("en-US")} stops. These jobs have ` +
    `${plural(createdStopCount, "stop")} and ${plural(selectedStopCount, "stop")} ` +
    `${selectedStopCount === 1 ? "is" : "are"} already selected. Select the jobs you need in the Jobs grid instead.`;
  const issues = result?.issues ?? [];

  // Selecting is offered whenever created orders are ready: after a full success, or for the created part of a
  // partially failed import
  const canSelect = createStatus === "done" || (createStatus === "failed" && createdOrders.length > 0);

  const failMessage = (() => {
    const {created, total} = createCounts;
    if (failReason === "failed") {
      const counts =
        `${plural(total - created, "order")} of ${total.toLocaleString("en-US")} could not be created ` +
        `(${created.toLocaleString("en-US")} created).`;
      const detail = failedOrders.length === 0 && failDetail ? ` ${failDetail}.` : "";
      const next = createdOrders.length
        ? " The created orders are listed below and can be selected for planning."
        : " Check the Jobs grid before importing the file again.";
      return counts + detail + next;
    }
    if (failReason === "unconfirmed")
      return (
        `The import status couldn't be checked after ${created.toLocaleString("en-US")} of ` +
        `${plural(total, "order")} were created. The rest may still be on their way, so check the Jobs grid ` +
        "in a few minutes before importing the file again."
      );
    return "The orders could not be created. Some of them may already exist, so check the Jobs grid before importing the file again.";
  })();

  const liveStatus = (() => {
    if (isParsing) return "Reading the file";
    if (stage === "select" && issues.length > 0) return `${plural(issues.length, "issue")} found`;
    if (stage === "preview" && result)
      return `${plural(orderCount, "order")}, ${plural(result.stopCount, "stop")} ready to import`;
    if (createStatus === "creating") return `Created ${createCounts.created} of ${plural(createCounts.total, "order")}`;
    if (createStatus === "syncing") return "Waiting for the new orders to be ready for planning";
    if (createStatus === "done") return `All ${plural(createdOrders.length, "order")} created`;
    if (createStatus === "failed") return failMessage;
    return "";
  })();

  return (
    // Neither Esc nor a click outside closes it: an accidental key press or click shouldn't drop a picked file or a preview
    <ImportDialog
      isOpen={isOpen}
      onClose={handleClose}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      title="Upload Jobs via File"
    >
      <DialogBody>
        <div ref={timelineRef}>
          <CsvImportTimeline currentStep={STAGE_INDEX[stage]} isFinished={createStatus === "done"} />
        </div>
        {/* Kept mounted with a one-line status so screen readers announce the outcome, not every row */}
        <VisuallyHidden aria-live="polite" data-testid="csv-import-live-status">
          {liveStatus}
        </VisuallyHidden>

        {stage === "select" && (
          <>
            <Intro>
              <p>
                Select a CSV or TXT file that follows the import schema. Each row is one stop, and rows with the same
                OrderNumber are joined within one order. OrderNumber isn’t saved, and each order gets a new number when
                it’s created. To keep your own reference on the order, use OrderReference.
              </p>
              <LinkRow>
                <Button
                  variant="minimal"
                  size="small"
                  intent={Intent.PRIMARY}
                  icon={<FontAwesomeIcon icon={solid("eye")} />}
                  text={isExampleOpen ? "Hide schema and example" : "View schema and example"}
                  onClick={() => setIsExampleOpen((open) => !open)}
                />
                <Popover
                  placement="bottom-start"
                  minimal
                  content={
                    <Menu>
                      <MenuItem
                        text="CSV (comma-separated)"
                        label=".csv"
                        data-testid="csv-import-download-example-csv"
                        onClick={() => handleDownloadExample("csv")}
                      />
                      <MenuItem
                        text="TXT (tab-delimited)"
                        label=".txt"
                        data-testid="csv-import-download-example-txt"
                        onClick={() => handleDownloadExample("txt")}
                      />
                    </Menu>
                  }
                >
                  <Button
                    variant="minimal"
                    size="small"
                    intent={Intent.PRIMARY}
                    icon={<FontAwesomeIcon icon={solid("download")} />}
                    endIcon={<FontAwesomeIcon icon={solid("caret-down")} />}
                    text="Download example"
                    data-testid="csv-import-download-example"
                  />
                </Popover>
              </LinkRow>
            </Intro>
            <Collapse isOpen={isExampleOpen}>
              <Panel data-testid="csv-import-schema">
                <p>
                  <strong>Required columns:</strong> {CSV_IMPORT_REQUIRED_LABELS.join(", ")}
                </p>
                <p>
                  <strong>Optional columns:</strong> {OPTIONAL_LABELS.join(", ")}
                </p>
                <p>
                  <strong>Dates:</strong> 2026-10-20 08:00 or 10/20/2026 8:00 AM, in the stop&apos;s local time.
                  StopType is P (pickup), D (delivery) or B (both).
                </p>
                <p>
                  <strong>Example</strong> (3 orders, 9 stops). TXT files use tabs instead of commas.
                </p>
                <ExampleText data-testid="csv-import-example-text">{CSV_IMPORT_EXAMPLE}</ExampleText>
              </Panel>
            </Collapse>
            <DropZone
              isDragging={isDragging}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              data-testid="csv-import-drop-zone"
            >
              <HiddenInput
                type="file"
                accept=".csv,.txt"
                aria-describedby={HINT_ID}
                data-testid="csv-import-file-input"
                onChange={handleInputChange}
              />
              {isParsing ? (
                <Spinner size={32} />
              ) : (
                <DropIcon>
                  <FontAwesomeIcon icon={solid("cloud-arrow-up")} />
                </DropIcon>
              )}
              <DropTitle>{isParsing ? `Reading ${fileName}...` : "Drag and drop a CSV or TXT file here"}</DropTitle>
              <DropHint id={HINT_ID}>
                or <Browse>browse your files</Browse> · .csv or tab-delimited .txt · up to 5 MB and{" "}
                {Constants.CSV_IMPORT.MAX_ORDERS.toLocaleString("en-US")} orders
              </DropHint>
            </DropZone>
            {result && issues.length > 0 && (
              <IssuesCallout
                intent={Intent.DANGER}
                title={`${fileName} can't be imported. Fix these issues and choose the file again`}
                data-testid="csv-import-issues"
                role="region"
                aria-label="Import issues"
                tabIndex={0}
              >
                <IssueList>
                  {issues.map((issue, index) => (
                    <li key={`${index}-${issue}`}>{issue}</li>
                  ))}
                </IssueList>
              </IssuesCallout>
            )}
          </>
        )}

        {stage === "preview" && result && (
          <>
            <SummaryBar data-testid="csv-import-summary">
              <FileChip>
                <FontAwesomeIcon icon={solid("file-csv")} />
                {fileName}
              </FileChip>
              <Stat>{plural(orderCount, "order")}</Stat>
              <Stat>{plural(result.stopCount, "stop")}</Stat>
              {result.ignoredColumns.length > 0 && <Muted>Ignored columns: {result.ignoredColumns.join(", ")}</Muted>}
            </SummaryBar>
            <RoundedCallout intent={Intent.PRIMARY} icon={<FontAwesomeIcon icon={solid("circle-info")} />}>
              Order numbers are generated by the system when the orders are created. The OrderNumber in your file is
              only used to group stops into orders.
            </RoundedCallout>
            <CsvImportStopsTable
              orders={result.orders}
              orderNumberLabel="OrderNumber (from file)"
              testId="csv-import-preview-table"
            />
            <ConfirmPanel hasError={showConfirmError} data-testid="csv-import-confirm-panel">
              <ConfirmHeading>
                <FontAwesomeIcon icon={solid("triangle-exclamation")} />
                Confirm before creating orders
              </ConfirmHeading>
              <Checkbox
                checked={isConfirmed}
                inputRef={confirmInputRef}
                onChange={(event) => {
                  setIsConfirmed(event.currentTarget.checked);
                  if (event.currentTarget.checked) setShowConfirmError(false);
                }}
                data-testid="csv-import-confirm-checkbox"
                label={`I understand that ${plural(
                  orderCount,
                  "order"
                )} will be created in the system, and this can't be cancelled once it starts.`}
              />
              {showConfirmError && (
                <ConfirmError role="alert" data-testid="csv-import-confirm-error">
                  Tick the box to confirm before the orders are created.
                </ConfirmError>
              )}
            </ConfirmPanel>
          </>
        )}

        {stage === "create" && isCreating && (
          <Progress data-testid="csv-import-creating">
            <ProgressTitle>
              {createStatus === "creating"
                ? "Creating orders..."
                : createCounts.failed > 0
                ? `All ${plural(
                    createCounts.total,
                    "order"
                  )} processed. Waiting for the created ones to be ready for planning...`
                : `All ${plural(createCounts.total, "order")} created. Waiting for them to be ready for planning...`}
            </ProgressTitle>
            <CreateProgressBar intent={Intent.PRIMARY} value={progressValue} />
            <CreatedCount data-testid="csv-import-created-count">
              {createCounts.created.toLocaleString("en-US")} of {createCounts.total.toLocaleString("en-US")} created
            </CreatedCount>
            <Muted>
              You can close this window. The orders keep being created, and progress shows next to the Upload button.
            </Muted>
          </Progress>
        )}

        {stage === "create" && createStatus === "done" && (
          <>
            <RoundedCallout
              intent={Intent.SUCCESS}
              icon={<FontAwesomeIcon icon={solid("circle-check")} />}
              data-testid="csv-import-done"
            >
              All {plural(createdOrders.length, "order")} were created. Select them for planning now, or close and
              select them in the Jobs grid later.
            </RoundedCallout>
            <CsvImportJobsTable orders={createdOrders} testId="csv-import-created-table" />
          </>
        )}

        {stage === "create" && createStatus === "failed" && (
          <>
            <RoundedCallout
              intent={Intent.DANGER}
              icon={<FontAwesomeIcon icon={solid("triangle-exclamation")} />}
              data-testid="csv-import-failed"
            >
              {/* The callout is a flex row (icon + content): stack the message, the toggle and the list */}
              <FailBody>
                <span>{failMessage}</span>
                {failedOrders.length > 0 && (
                  <>
                    <ToggleErrorsButton
                      variant="minimal"
                      size="small"
                      intent={Intent.DANGER}
                      rightIcon={showFailedOrders ? "chevron-up" : "chevron-down"}
                      onClick={() => setShowFailedOrders((open) => !open)}
                      aria-expanded={showFailedOrders}
                      aria-controls="csv-import-failed-orders-list"
                      data-testid="csv-import-toggle-errors"
                    >
                      {showFailedOrders ? "Hide" : "View"} {plural(failedOrders.length, "error")}
                    </ToggleErrorsButton>
                    {showFailedOrders && (
                      <FailedList
                        id="csv-import-failed-orders-list"
                        // Focusable so the scrollable list can be scrolled from the keyboard
                        tabIndex={0}
                        aria-label="Failed orders"
                        data-testid="csv-import-failed-orders"
                      >
                        {failedOrders.map((order) => (
                          <li key={order.orderNumber}>
                            <strong>{order.orderNumber}</strong>: {order.error}
                          </li>
                        ))}
                      </FailedList>
                    )}
                  </>
                )}
              </FailBody>
            </RoundedCallout>
            {createdOrders.length > 0 && (
              <CsvImportJobsTable orders={createdOrders} testId="csv-import-created-table" />
            )}
          </>
        )}
      </DialogBody>
      <DialogFooter
        actions={
          <>
            {stage === "select" && <CancelButton onClick={handleClose}>Cancel</CancelButton>}
            {stage === "preview" && (
              <>
                <CancelButton onClick={reset}>Choose another file</CancelButton>
                <StyledButton intent="primary" onClick={handleCreateClick} data-testid="csv-import-confirm">
                  Create {plural(orderCount, "order")}
                </StyledButton>
              </>
            )}
            {/* A finished import, even one where every order failed, can be followed by a new one */}
            {stage === "create" && !isCreating && (
              <CancelButton onClick={reset} data-testid="csv-import-new">
                New Import
              </CancelButton>
            )}
            {stage === "create" && canSelect && (
              // The reason the button is disabled shows on hover (the tooltip wrapper receives the hover)
              <Tooltip
                content={stopLimitMessage}
                placement="top"
                disabled={!isOverStopLimit}
                popoverClassName="csv-import-stop-limit-tooltip"
              >
                <StyledButton
                  intent="primary"
                  disabled={isOverStopLimit}
                  onClick={handleSelectForPlanning}
                  data-testid="csv-import-select"
                >
                  Select All {createdOrders.length.toLocaleString("en-US")} for Planning and Close
                </StyledButton>
              </Tooltip>
            )}
            {stage === "create" && <CancelButton onClick={handleClose}>Close</CancelButton>}
          </>
        }
      />
    </ImportDialog>
  );
};

// Scrolls so a long error list doesn't push the created jobs out of view
const FailedList = styled.ul`
  align-self: stretch;
  margin: 0;
  padding: 8px 8px 8px 28px;
  max-height: 140px;
  overflow: auto;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.6);

  li {
    margin: 2px 0;
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.COLOR_BORDER_ERROR};
    outline-offset: 2px;
  }
`;

const FailBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  flex: 1;
`;

const ToggleErrorsButton = styled(Button)`
  margin-left: -8px;
`;

const ImportDialog = styled(StyledDialog)`
  width: 1080px;
  max-width: 95vw;
  border-radius: 14px;
  overflow: hidden;

  .bp5-dialog-header {
    padding: 12px 12px 4px 24px;
    border-radius: 14px 14px 0 0;
  }

  .bp5-dialog-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-height: 70vh;
    margin: 0;
    padding: 12px 24px 20px;
    overflow-y: auto;
  }

  .bp5-dialog-footer {
    padding: 12px 24px 16px;
    border-top: 1px solid ${COLORS.COLOR_BORDER_LIGHT};
  }

  .bp5-button {
    border-radius: 8px;
  }
`;

const VisuallyHidden = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  p {
    margin: 0;
    color: ${COLORS.GRAY_800};
  }
`;

const LinkRow = styled.div`
  display: flex;
  gap: 4px;
  margin-left: -8px;
`;

const ExampleText = styled.pre`
  margin: 0;
  padding: 10px 12px;
  overflow-x: auto;
  border: 1px solid ${COLORS.COLOR_BORDER_LIGHT};
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.6;
  background: ${COLORS.WHITE};
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${COLORS.BG_GRAY_LIGHT};

  p {
    margin: 0;
  }
`;

const DropZone = styled.label<{isDragging: boolean}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 16px;
  border: 2px dashed ${(props) => (props.isDragging ? COLORS.BLUE : COLORS.COLOR_BORDER)};
  border-radius: 14px;
  background: ${(props) => (props.isDragging ? COLORS.BLUE_VERY_LIGHT : COLORS.WHITE)};
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${COLORS.BLUE_LIGHT};
    background: ${COLORS.BLUE_VERY_LIGHT};
  }

  &:focus-within {
    border-color: ${COLORS.BLUE};
    box-shadow: 0 0 0 3px ${COLORS.BLUE_100};
  }
`;

// Visually hidden but focusable, so the drop zone stays keyboard-operable
const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
`;

const DropIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  font-size: 24px;
  color: ${COLORS.BLUE};
  background: ${COLORS.BLUE_100};
`;

const DropTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${COLORS.GRAY_800};
`;

const DropHint = styled.span`
  color: ${COLORS.TEXT_GRAY_MEDIUM};
`;

const Browse = styled.span`
  color: ${COLORS.BLUE};
  font-weight: 600;
`;

// Callouts in the steps take a FontAwesome icon, which Blueprint doesn't position; lay the icon and
// message out as a row so the icon stays vertically centred on the message
const RoundedCallout = styled(Callout)`
  border-radius: 10px;

  &.bp5-callout.bp5-callout-icon {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
  }

  /* Stays on the first line when the content grows (error toggle and list) */
  & > .svg-inline--fa:first-of-type {
    flex-shrink: 0;
    font-size: 16px;
    margin-top: 2px;
  }
`;

const ConfirmPanel = styled.div<{hasError: boolean}>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  border: 1px solid ${(props) => (props.hasError ? COLORS.COLOR_BORDER_ERROR : COLORS.ORANGE_BORDER)};
  border-radius: 12px;
  background: ${COLORS.ORANGE_SOFT};
  box-shadow: ${(props) => (props.hasError ? `0 0 0 3px ${COLORS.RED_SOFT}` : "none")};

  .bp5-control {
    margin: 0;
    color: ${COLORS.GRAY_800};
  }
`;

const ConfirmHeading = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: ${COLORS.TEXT_ORANGE};
`;

const ConfirmError = styled.span`
  color: ${COLORS.COLOR_BORDER_ERROR};
  font-weight: 600;
`;

// Keeps Blueprint's own icon and title layout
const IssuesCallout = styled(Callout)`
  border-radius: 10px;
  max-height: 280px;
  overflow-y: auto;
`;

const IssueList = styled.ul`
  margin: 0;
  padding-left: 20px;
`;

const SummaryBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const FileChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: 600;
  color: ${COLORS.GRAY_800};
  background: ${COLORS.BG_GRAY_LIGHT};
`;

const Stat = styled.span`
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: 600;
  color: ${COLORS.BLUE_DARK};
  background: ${COLORS.BLUE_VERY_LIGHT};
`;

const Muted = styled.span`
  color: ${COLORS.TEXT_GRAY_MEDIUM};
`;

const Progress = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 16px;
  border-radius: 14px;
  background: ${COLORS.BG_GRAY_LIGHT};
  text-align: center;
`;

const CreateProgressBar = styled(ProgressBar)`
  max-width: 420px;
  height: 10px;
  border-radius: 999px;

  .bp5-progress-meter {
    border-radius: 999px;
  }
`;

const CreatedCount = styled.span`
  font-weight: 600;
  color: ${COLORS.GRAY_800};
`;

const ProgressTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${COLORS.GRAY_800};
`;

export default CsvImportDialog;
